import { Form, Formik } from 'formik';
import { NextPage } from 'next';
import { useRouter } from 'next/router';
import { Ref, useRef, useState } from 'react';
import { Trans, useTranslation } from 'next-i18next';
import { useAsync } from 'react-use';
import ErrorFocus from '../../components/forms/ErrorFocus';
import PageLayout from '../../components/layout/PageLayout';
import Panel from '../../components/layout/Panel';
import JoinTeacherTribeForm from '../../components/mailchimp/join-teacher-tribe-form';
import Seo from '../../components/seo/Seo';
import Button from '../../components/ui/button/Button';
import {
    getInterestsSchema,
    getMergeFields,
    InterestsSchema,
    MergeFieldSchema,
    postJoinTeacherTribe
} from '../../lib/api/marketing.api';
import { JoinTeacherTribeFormModel, Yup } from '@model/dist/esm'
import ReCAPTCHA from "react-google-recaptcha";
import { Loading } from '../../components/misc/Loading';
import Footer from '../../components/footer/Footer';
import { Routes } from 'lib/routes';
import { useNavbarHeader } from 'components/navbar/NavbarHeaderContext';
import LicenceAgreementCheckbox from 'components/form-controls/controls/LicenceAgreementCheckbox';
import * as Sentry from '@sentry/browser';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { defaultNs } from 'lib/i18n/defaults';
import RetryableReCAPTCHA from 'components/forms/RetryableReCaptcha';
import CheckboxControl from 'components/form-controls/controls/CheckboxControl';
import { useModalError } from 'components/errors/error-modal-context';

interface JoinTeacherTribeFormik {
    email: string;
    firstName?: string;
    lastName?: string;
    licenceAgreement: boolean;
    knowFrom?: string;
    locale?: string;
    interests: {
        [key: string]: boolean;
    };
    categories: {
        [key: string]: string[];
    };
    marketingAgreement: boolean;
}

const JoinTeacherTribePage: NextPage = () => {

    const { locale, ...router } = useRouter();
    const { navbarHeaderRef } = useNavbarHeader()
    const reCAPTCHARef: Ref<ReCAPTCHA> = useRef();
    const { t } = useTranslation();
    const { dispatchError } = useModalError()
    const initialValues: JoinTeacherTribeFormik = {
        email: '',
        firstName: '',
        lastName: '',
        licenceAgreement: false,
        knowFrom: null,
        interests: {},
        categories: {},
        marketingAgreement: true,
    };
    const validationSchema = Yup.object({
        email: Yup.string().email().required(),
        firstName: Yup.string().required(),
        lastName: Yup.string().required(),
        licenceAgreement: Yup.boolean().required().isTrue({ key: 'common:licenceAgreementKO' }),
    })
    const [loading, setLoading] = useState(false)
    const [disabled, setDisabled] = useState(false)
    const [interestSchema, setInterestSchema] = useState<InterestsSchema>(null)
    const [mergeFields, setMergeFields] = useState<MergeFieldSchema>(null)
    const subscribeValuesRef = useRef<JoinTeacherTribeFormModel>(null);


    useAsync(async () => {
        const schema = await getInterestsSchema()
        setInterestSchema(schema);
    }, [])

    useAsync(async () => {
        const mergeFields = await getMergeFields();
        setMergeFields(mergeFields);
    }, [])


    const handleSubmit = (values: JoinTeacherTribeFormik, { setSubmitting }) => {
        setTimeout(async () => {
            // on récupère les interest des selects
            const selectInterests = {}
            Object.values(values.categories).flat().forEach(interest => {
                selectInterests[interest] = true
            })
            const subscribe: JoinTeacherTribeFormModel = {
                email: values.email,
                firstName: values.firstName,
                lastName: values.lastName,
                knowFrom: values.knowFrom,
                interests: {
                    ...values.interests,
                    ...selectInterests
                },
                marketingAgreement: values.marketingAgreement,
                locale: locale,
            }
            console.log(subscribe)
            subscribeValuesRef.current = subscribe

            setSubmitting(false);
            setLoading(true)
            setDisabled(true)

            reCAPTCHARef.current.execute();

        }, 400);
    }

    const reCAPTCHAOnExecute = async (captchaCode) => {
        try {
            const data = await postJoinTeacherTribe(subscribeValuesRef.current, captchaCode)
            router.replace({
                pathname: `${Routes.JOIN_TEACHER_TRIBE_CONFIRM}`,
                query: {
                    firstName: data.firstName
                }
            })
        } catch (err) {
            const response = err.response;
            if (response && response.status === 422) {
                dispatchError(t(`errors:${response.data}`), null)
            } else {
                dispatchError(t(`errors:apiError`), null)
            }
            console.log(err)
            Sentry.captureException(err);
        } finally {
            // Reset the reCAPTCHA when the request has failed or succeeeded
            // so that it can be executed again if user submits another email.
            reCAPTCHARef.current.reset();
            subscribeValuesRef.current = null;
            setLoading(false)
            setDisabled(false)
        }
    }

    const reCAPTCHAOnFail = async () => {
        setLoading(false)
    }

    const formReady = interestSchema && mergeFields

    return (
        <>
            <Seo pageKey="joinTeacherTribe" />
            <header className="relative z-10 bg-white shadow-header" ref={navbarHeaderRef}>
                <PageLayout py="md">
                    <div className="text-sm font-extrabold text-center uppercase text-gray-650">
                        {t('join-teacher-tribe:teacher')}
                    </div>
                    <h1 className="text-4xl font-extrabold text-center">
                        {t('join-teacher-tribe:title')}
                    </h1>
                    <div className="hidden grid-cols-1 grid-rows-3 my-10 lg:grid md:grid-cols-3 md:grid-rows-1 md:mt-16 gap-y-6">
                        <div className="grid items-center content-center grid-cols-4 grid-rows-2">
                            <span className="row-span-2 font-bold text-gray-400 text-8xl md:text-9xl">1</span>
                            <span className="col-span-3 text-xl font-bold md:text-2xl">{t('join-teacher-tribe:process.subscribe')}</span>
                            <span className="self-start col-span-3">
                                {t('join-teacher-tribe:process.subscribeLink')}&nbsp;{t('join-teacher-tribe:process.subscribeText')}
                            </span>
                        </div>
                        <div className="grid items-center content-center grid-cols-4 grid-rows-2">
                            <span className="row-span-2 font-bold text-gray-400 text-8xl md:text-9xl">2</span>
                            <span className="col-span-3 text-xl font-bold md:text-2xl">{t('join-teacher-tribe:process.receive')}</span>
                            <span className="self-start col-span-3">{t('join-teacher-tribe:process.receiveText')}</span>
                        </div>
                        <div className="grid items-center content-center grid-cols-4 grid-rows-2">
                            <span className="row-span-2 font-bold text-gray-400 text-8xl md:text-9xl">3</span>
                            <span className="col-span-3 text-xl font-bold md:text-2xl">{t('join-teacher-tribe:process.apply')}</span>
                            <span className="self-start col-span-3">{t('join-teacher-tribe:process.applyText')}</span>
                        </div>
                    </div>
                </PageLayout>
            </header>
            <div className="bg-gray-100">
                <PageLayout py="md" width="sm">
                    {formReady ? (
                        <>
                            <Panel title={t('join-teacher-tribe:panelTitle')} subTitle={t('join-teacher-tribe:panelSubText')}>
                                <Formik
                                    initialValues={initialValues}
                                    validationSchema={validationSchema}
                                    onSubmit={handleSubmit}
                                >
                                    <Form>

                                        <JoinTeacherTribeForm
                                            schema={interestSchema}
                                            mergeFields={mergeFields} />

                                        <LicenceAgreementCheckbox color="black" className="mt-4" />
                                        <CheckboxControl
                                            name="marketingAgreement"
                                            color="black"
                                            initialChecked={true}
                                        >
                                            {t('common:marketingAgreement')}
                                        </CheckboxControl>

                                        <ErrorFocus />

                                        <RetryableReCAPTCHA
                                            ref={reCAPTCHARef}
                                            size="invisible"
                                            onExecute={reCAPTCHAOnExecute}
                                            onFail={reCAPTCHAOnFail}
                                        />
                                        <Button htmlType="submit" className="w-full mt-10 primary" loading={loading} disabled={disabled}>
                                            {t('join-teacher-tribe:subscribe')}
                                        </Button>
                                    </Form>
                                </Formik>
                            </Panel>
                            <p className="mt-5 text-sm text-gray-650">
                                {t('join-teacher-tribe:legalText')}
                            </p>
                        </>
                    ) : (
                        <Loading />
                    )}
                </PageLayout>
            </div>
            <Footer />

        </>
    )
}

export default JoinTeacherTribePage;


export const getStaticProps = async ({ locale }) => ({
    props: {
        ...await serverSideTranslations(locale, [...defaultNs, 'join-teacher-tribe']),
    }
})
